import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Sidebar } from '../Sidebar/Sidebar';
import { TenantOrganicBackground } from './TenantOrganicBackground';
import { AppMode, LeadPageType, ClientPageType } from '@/shared/types/appTypes';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';

interface TailwindPlusLayoutProps {
  children: React.ReactNode;
  mode: AppMode;
  onModeChange: (mode: AppMode) => void;
  currentPage: LeadPageType | ClientPageType;
  leadCount?: number;
  clientCount?: number;
}

export const TailwindPlusLayout: React.FC<TailwindPlusLayoutProps> = ({
  children,
  currentPage,
  leadCount = 0,
  clientCount = 0,
}) => {
  const location = useLocation();
  const { user: authUser } = useAuth();

  // Tenant-scoped roles get the organic gradient configured in Company.portal_theme;
  // platform users keep the hardcoded canvas. Easy to expand later (drop this check).
  const useTenantBackground =
    authUser?.role === 'owly_broker' || authUser?.role === 'owly_agency';

  // Toggle html.tenant-bg so the CSS override kicks in (makes html/body
  // transparent so the fixed organic gradient actually shows through the default
  // gray-50 canvas that would otherwise cover it).
  useEffect(() => {
    document.documentElement.classList.toggle('tenant-bg', useTenantBackground);
    return () => {
      document.documentElement.classList.remove('tenant-bg');
    };
  }, [useTenantBackground]);

  // Ref para el contenedor principal
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Animar transiciones de página
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (mainContentRef.current) {
        // Animación de fade in desde arriba
        gsap.fromTo(
          mainContentRef.current,
          {
            opacity: 0,
            y: 10,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: 'power2.out',
          }
        );
      }
    });

    return () => ctx.revert();
  }, [location.pathname]); // Se ejecuta cada vez que cambia la ruta

  return (
    <div className="flex h-full relative overflow-hidden text-gray-900 dark:text-gray-100">
      {useTenantBackground ? (
        /* Brokers/agencies: MISMO patrón que PortalPreview (editor) y BrandedBackground
           (Cotizador) — base + linear + 4-corner radial mesh. Match 1:1 con el preview. */
        <TenantOrganicBackground />
      ) : (
        /* Plataforma: mesh original blue/orange con 5 radiales */
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-orange-50/30 to-blue-100/50 dark:from-[#2D2D2D] dark:via-[#2F2F2F] dark:to-[#2D2D2D]">
          <div className="absolute inset-0 dark:hidden" style={{
            backgroundImage: `
              radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
              radial-gradient(at 100% 0%, rgba(251, 146, 60, 0.12) 0px, transparent 50%),
              radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.1) 0px, transparent 50%),
              radial-gradient(at 0% 100%, rgba(251, 146, 60, 0.15) 0px, transparent 50%),
              radial-gradient(at 50% 50%, rgba(147, 197, 253, 0.08) 0px, transparent 50%)
            `
          }}></div>
          <div className="absolute inset-0 hidden dark:block" style={{
            backgroundImage: `
              radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.06) 0px, transparent 50%),
              radial-gradient(at 100% 0%, rgba(251, 146, 60, 0.04) 0px, transparent 50%),
              radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.05) 0px, transparent 50%),
              radial-gradient(at 0% 100%, rgba(251, 146, 60, 0.06) 0px, transparent 50%),
              radial-gradient(at 50% 50%, rgba(100, 100, 100, 0.04) 0px, transparent 50%)
            `
          }}></div>
        </div>
      )}

      {/* Contenido (encima del fondo) */}
      <div className="relative z-10 flex w-full h-full overflow-hidden">
      {/* Sidebar - Navegación de dos niveles */}
      <div className="flex-shrink-0 h-full overflow-visible">
        <Sidebar
          currentPage={currentPage}
          leadCount={leadCount}
          clientCount={clientCount}
        />
      </div>

      {/* Main Content Area - Ocupa el espacio central */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <main ref={mainContentRef} className="flex-1 min-h-0 overflow-hidden">
          {children}
        </main>
      </div>

      {/* AI Mini Chat - Desactivado temporalmente */}
      {/* <AIMiniChat
        onCommand={emitCommand}
        currentPage={currentPage}
        viewMode={leadsPageContext?.viewMode}
        userId={currentUser.id}
        userName={currentUser.name}
        projectId={aiProfileId}
        context={{
          totalLeads: leads.length,
          visibleLeads: leads.length,
          selectedCount: 0,
          currentFilters: {},
          currentView: 'all',
          currentLayout: 'grid',
        }}
      /> */}
      </div>
    </div>
  );
};